import React, { useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { CONTENT } from '../content';
import { getRoadmapEntries, flattenMilestones } from '../../domain/roadmap/roadmapMilestones';
import { useTokenomics } from '../useTokenomics';
import { RibbonMarquee } from '../RibbonMarquee';
import { RedesignComicViewer } from '../RedesignComicViewer';
import { RedesignNav, RedesignFooter } from '../SharedComponents';

const ComicGlobalStyle = createGlobalStyle`
  @font-face {
    font-family: 'BreakingTheComic';
    src: url('/assets/fonts/BreakingTheComic.woff2') format('woff2');
  }
  @font-face {
    font-family: 'GROBOLD';
    src: url('/assets/fonts/GROBOLD.woff2') format('woff2');
  }
  body, html {
    margin: 0;
    padding: 0;
    background-color: #F7E03C; /* Brand Yellow */
    background-image: radial-gradient(#dca400 15%, transparent 16%), radial-gradient(#dca400 15%, transparent 16%);
    background-size: 20px 20px;
    background-position: 0 0, 10px 10px;
    font-family: 'BreakingTheComic', sans-serif;
    color: #000;
    overflow-x: hidden;
  }
  * { box-sizing: border-box; }
`;

const Container = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  padding: 40px 20px;
  display: flex;
  flex-direction: column;
  gap: 60px;
  @media (max-width: 768px) {
    gap: 30px;
    padding: 20px 15px;
  }
`;

const ComicPanelRow = styled.div`
  display: flex;
  gap: 20px;
  width: 100%;
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ComicPanel = styled(motion.div)<{ $bgImage?: string, $bgColor?: string }>`
  flex: 1;
  background-color: ${props => props.$bgColor || '#fff'};
  ${props => props.$bgImage && `background-image: url(${props.$bgImage}); background-size: cover; background-position: center;`}
  border: 6px solid #000;
  box-shadow: 12px 12px 0 rgba(0,0,0,1);
  padding: 40px;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  color: ${props => props.$bgColor === '#3D8DF5' ? '#fff' : '#000'};
  
  @media (max-width: 768px) {
    padding: 20px;
    border-width: 4px;
    box-shadow: 6px 6px 0 #000;
  }
`;

const TitleImg = styled(motion.img)`
  max-width: 100%;
  height: auto;
  margin-bottom: 20px;
  z-index: 2;
  filter: drop-shadow(4px 4px 0 #000);
`;

const FloatingChar = styled(motion.img)`
  position: absolute;
  z-index: 10;
  filter: drop-shadow(4px 4px 0 #000);
`;

const ActionBtn = styled(motion.a)`
  background: #F7E03C;
  color: #000;
  font-family: 'GROBOLD', sans-serif;
  font-size: 2rem;
  padding: 15px 30px;
  border: 4px solid #000;
  box-shadow: 6px 6px 0 #000;
  text-decoration: none;
  display: inline-block;
  cursor: pointer;
  text-align: center;
  z-index: 2;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    padding: 10px 20px;
    box-shadow: 4px 4px 0 #000;
  }
`;

const CopyBtn = styled(motion.div)`
  background: #fff;
  color: #000;
  border: 4px solid #000;
  padding: 10px 20px;
  font-family: 'Inter', sans-serif;
  font-weight: bold;
  cursor: pointer;
  box-shadow: 4px 4px 0 #000;
  margin-top: 20px;
  display: inline-block;
  z-index: 2;
`;

const ParagraphText = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 1.2rem;
  line-height: 1.6;
  z-index: 2;
  position: relative;
  background: rgba(255,255,255,0.9);
  padding: 20px;
  border: 4px solid #000;
  border-radius: 8px;
  color: #000;
  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 15px;
  }
`;

const TokenomicsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
  z-index: 2;
  position: relative;
`;

const TokenCard = styled(motion.a)`
  background: #fff;
  border: 4px solid #000;
  padding: 20px;
  text-decoration: none;
  color: #000;
  box-shadow: 4px 4px 0 #000;
  display: flex;
  flex-direction: column;
  
  h4 {
    font-family: 'GROBOLD', sans-serif;
    font-size: 1.5rem;
    margin: 0 0 10px 0;
    color: #3D8DF5;
  }
  p {
    font-family: 'Inter', sans-serif;
    margin: 0;
  }
`;

export default function RedesignA() {
  const roadmap = flattenMilestones(getRoadmapEntries());
  const { data: tokenomics } = useTokenomics();
  const [copied, setCopied] = useState(false);

  const copyCA = () => {
    navigator.clipboard.writeText(CONTENT.hero.ca);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const pressProps = {
    whileHover: { scale: 1.05, y: -2, boxShadow: "8px 8px 0 #000" },
    whileTap: { scale: 0.95, y: 4, boxShadow: "0px 0px 0 #000" },
    transition: { type: "spring" as const, stiffness: 400, damping: 17 }
  };
  
  const charHover = {
    whileHover: { scale: 1.15, rotate: [-5, 5, -5, 0], y: -10 },
  };

  return (
    <>
      <ComicGlobalStyle />
      <RedesignNav />
      <RibbonMarquee />
      
      <Container>
        {/* HERO ROW */}
        <ComicPanelRow>
          <ComicPanel $bgImage="/assets/img/hero/bg.webp" $bgColor="#3D8DF5" style={{ flex: 1.5 }}>
            <TitleImg src="/assets/img/hero/title.webp" alt="ZEUS" />
            <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <ActionBtn href={CONTENT.hero.buyLink} target="_blank" {...pressProps}>
                BUY ZEUS
              </ActionBtn>
              <CopyBtn onClick={copyCA} {...pressProps}>
                {copied ? "COPIED!" : `CA: ${CONTENT.hero.ca}`}
              </CopyBtn>
            </div>
            <FloatingChar 
              src="/assets/img/join/01.webp" 
              style={{ bottom: '-10%', right: '-5%', width: '45%', maxWidth: '350px', minWidth: '150px' }}
              {...charHover}
            />
          </ComicPanel>
          <ComicPanel $bgColor="#8CC4F8" style={{ padding: '0', flex: 1, display: 'flex' }}>
            <video 
              controls 
              poster="/assets/img/hero/bg.webp"
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            >
              <source src="/assets/img/video/01.mp4" type="video/mp4" />
            </video>
          </ComicPanel>
        </ComicPanelRow>

        <RibbonMarquee />

        {/* ABOUT ROW */}
        <ComicPanelRow>
          <ComicPanel $bgImage="/assets/img/hero/bg.webp" $bgColor="#3D8DF5">
            <TitleImg src="/assets/img/about/title-pepesdog.webp" />
            <ParagraphText>
              {CONTENT.about.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
            </ParagraphText>
            <FloatingChar 
              src="/assets/img/about/01.webp" 
              style={{ bottom: '-5%', right: '-5%', width: '40%', maxWidth: '300px', minWidth: '150px' }}
              {...charHover}
            />
          </ComicPanel>
          <ComicPanel $bgColor="#fff">
            <TitleImg src="/assets/img/about-cto/title.webp" />
            <ParagraphText>
              {CONTENT.aboutCto.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
            </ParagraphText>
            <FloatingChar 
              src="/assets/img/about-cto/01.webp" 
              style={{ bottom: '10%', right: '10%', width: '35%', maxWidth: '280px', minWidth: '140px' }}
              {...charHover}
            />
          </ComicPanel>
        </ComicPanelRow>

        <RibbonMarquee />

        {/* TOKENOMICS ROW */}
        <ComicPanel $bgColor="#8CC4F8">
          <TitleImg src="/assets/img/tokenomics/title.webp" style={{ alignSelf: 'center' }} />
          <TokenomicsGrid>
            {CONTENT.tokenomics.cards.map((card, i) => (
              <TokenCard href={card.link} target="_blank" key={i} {...pressProps}>
                <h4>{card.title}</h4>
                <p>{card.text}</p>
              </TokenCard>
            ))}
          </TokenomicsGrid>
          
          {tokenomics && (
            <div style={{ background: '#fff', border: '4px solid #000', padding: '20px', marginTop: '20px', display: 'flex', justifyContent: 'space-around', alignItems: 'center', boxShadow: '6px 6px 0 #000', zIndex: 2, flexWrap: 'wrap', gap: '20px' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: 'GROBOLD', fontSize: '1.5rem' }}>LP LOCKED</div>
                <div style={{ fontFamily: 'BreakingTheComic', fontSize: '2.5rem', color: '#3D8DF5' }}>{tokenomics.lpZeusPct.toFixed(1)}%</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: 'GROBOLD', fontSize: '1.5rem' }}>CIRCULATING</div>
                <div style={{ fontFamily: 'BreakingTheComic', fontSize: '2.5rem', color: '#3D8DF5' }}>{tokenomics.circulatingPct.toFixed(1)}%</div>
              </div>
            </div>
          )}
          <FloatingChar 
            src="/assets/img/tokenomics/01.webp" 
            style={{ top: '5%', right: '5%', width: '25%', maxWidth: '200px', minWidth: '100px' }}
            {...charHover}
          />
        </ComicPanel>

        <RibbonMarquee />

        {/* ROADMAP & ARTIST ROW */}
        <ComicPanelRow>
          <ComicPanel $bgColor="#fff" style={{ flex: 1.5 }}>
            <TitleImg src="/assets/img/roadmap/title.webp" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', zIndex: 2 }}>
              {roadmap.map((ms, i) => (
                <motion.div 
                  key={i} 
                  style={{ background: '#F7E03C', border: '4px solid #000', padding: '15px', boxShadow: '4px 4px 0 #000', display: 'flex', flexDirection: 'column', gap: '10px' }}
                  {...pressProps}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, fontFamily: 'GROBOLD', fontSize: '1.3rem' }}>{ms.title}</h3>
                    <span style={{ background: '#fff', border: '2px solid #000', padding: '3px 8px', fontFamily: 'GROBOLD' }}>{ms.status}</span>
                  </div>
                  <p style={{ margin: 0, fontFamily: 'Inter', fontSize: '1.1rem' }}>{ms.description}</p>
                </motion.div>
              ))}
            </div>
          </ComicPanel>
          <ComicPanel $bgImage="/assets/img/hero/bg.webp" $bgColor="#3D8DF5" style={{ flex: 1 }}>
            <TitleImg src="/assets/img/artist/title.webp" />
            <ParagraphText>
              {CONTENT.artist.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
            </ParagraphText>
            <FloatingChar 
              src="/assets/img/artist/01.webp" 
              style={{ bottom: '-5%', right: '10%', width: '50%', maxWidth: '300px', minWidth: '150px' }}
              {...charHover}
            />
          </ComicPanel>
        </ComicPanelRow>

        <RibbonMarquee />

        {/* MEMES */}
        <ComicPanel $bgColor="#8CC4F8">
          <TitleImg src="/assets/img/memes/title.webp" style={{ alignSelf: 'center' }} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '15px', zIndex: 2 }}>
            {[1, 2, 3, 4, 5, 6].map(num => (
              <motion.img 
                key={num} 
                src={`/assets/img/memes/0${num}.webp`} 
                style={{ width: '100%', border: '4px solid #000', boxShadow: '4px 4px 0 #000', borderRadius: '4px' }}
                whileHover={{ scale: 1.15, zIndex: 10, rotate: num % 2 === 0 ? 5 : -5 }}
              />
            ))}
          </div>
        </ComicPanel>

        {/* COMIC SECTION */}
        <ComicPanel $bgColor="#fff" style={{ padding: '0', border: 'none', boxShadow: 'none' }}>
          <RedesignComicViewer />
        </ComicPanel>

        <RibbonMarquee />

        {/* JOIN */}
        <ComicPanelRow>
          <ComicPanel $bgColor="#3D8DF5" style={{ alignItems: 'center', textAlign: 'center', minHeight: '400px', justifyContent: 'center' }}>
            <h2 style={{ fontFamily: 'GROBOLD', fontSize: '4rem', color: '#F7E03C', textShadow: '4px 4px 0 #000', margin: '0 0 40px 0', zIndex: 2 }}>JOIN THE ARMY</h2>
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', justifyContent: 'center', zIndex: 2 }}>
              {Object.entries(CONTENT.social).map(([key, link]) => (
                <ActionBtn href={link} target="_blank" key={key} {...pressProps}>
                  {key}
                </ActionBtn>
              ))}
            </div>
            <FloatingChar 
              src="/assets/img/join/01.webp" 
              style={{ bottom: '10%', left: '5%', width: '30%', maxWidth: '250px', minWidth: '120px' }}
              {...charHover}
            />
          </ComicPanel>
        </ComicPanelRow>

      </Container>
      
      <RedesignFooter />
    </>
  );
}
